/**
 * formStore
 *
 * Unique Valtio singleton that mirrors the current route. Holds selection IDs,
 * documents + collections backing the page, and page-local UI state.
 *
 * Firestore binding is driven by subscribing to selection changes: when
 * `formId`, `versionId`, `uiLayoutId`, or the derived `formType` changes,
 * the corresponding `persistDocument` / `persistCollection` listener is
 * attached/reattached/detached.
 *
 * `selectedSplitOption` and `splitQuery` are one unit of state, always written
 * together through `clearSplitSelection` here or `setSelectedSplitOption` in
 * `formActions` — see the `splitQuery` field JSDoc for the invariant and for why
 * the filter is stored rather than derived from the live options.
 *
 * The `formUndoStack` is dropped wherever the loaded tables are (form change,
 * version change, `reset`): an undo entry names a doc and a path, so an entry
 * recorded on form A would otherwise still be poppable on form B. The
 * `formRecoveryStore` is dropped at the same three points and for the same
 * reason — a point-in-time read taken against form A must not be previewable on
 * form B.
 *
 * Read via `useSnapshot(formStore.state)` (or via the `useFormStore` hook).
 * Navigate via `formStore.navigate` (installed by `formStoreUrlListener`).
 */

import { proxy, subscribe } from 'valtio';
import { proxyMap } from 'valtio/utils';
import type { NavigateFunction } from 'react-router-dom';
import type {
    Form,
    FormType,
    FormVersion,
    SplitQuery,
    UILayout,
    VisibilityTypeId,
} from '#types/System.types';
import { DEFAULT_VISIBILITY_TYPES, SYSTEM_DEFAULT_VISIBILITY_VIEW, type OrganizationParameters } from '#types/System.Parameters.types';
import type {
    DocumentPersist,
    CollectionPersist,
    DocumentState,
    CollectionSlot,
    DocEntry,
} from '../Store.types';
import { persistDocument } from '../persist/persistDocument';
import { persistCollection } from '../persist/persistCollection';
import { createArTableState, type ArTableUIState } from '../../components/ArTable/state/createArTableState';
import { createDocumentStore } from '../singleton/createDocumentStore';
import { resolveVisibilityView } from './resolveVisibilityView';
import { formRecoveryStore } from './formRecoveryStore';
import { formUndoStack } from './formUndoStack';

/**
 * Handle to the app-wide `parameters/organization` singleton (registry-backed
 * by path, so this is the same instance `UserProvider` connects). Read-only
 * here: used to resolve the on-open visibility view (SYD-2080).
 */
const orgParametersStore = createDocumentStore<OrganizationParameters & Record<string, unknown>>('parameters/organization');

export type FormTableSlot = {
    ui: ArTableUIState;
};

/**
 * One required element the user still has to fill, as reported by
 * `checkRequiredFields`. `fieldId` carries the entity-reference key when
 * `isEntityReference` is true, in which case `entityId` names the entity so the
 * modal can label it without re-walking the layout.
 */
export type MissingRequiredField = {
    fieldId: string;
    isEntityReference: boolean;
    isVisibleInCurrentMode: boolean;
    entityId?: string;
};

export type FormStoreState = {
    formId: string | undefined;
    versionId: string | undefined;
    uiLayoutId: string | undefined;

    form: DocumentState<Form>;
    formVersion: DocumentState<FormVersion>;
    formType: DocumentState<FormType>;
    uiLayout: DocumentState<UILayout>;

    formVersions: CollectionSlot<FormVersion>;
    formTypes: CollectionSlot<FormType>;
    uiLayouts: CollectionSlot<UILayout>;

    visibilityMode: VisibilityTypeId;
    /**
     * Per-form-type memory of the chosen view. Written only on an explicit
     * `setVisibilityMode`; a form type present here restores its remembered
     * view on navigation and opts out of default resolution (SYD-2080).
     */
    sessionViewByFormType: Record<string, VisibilityTypeId>;
    /**
     * Current user's access group ids, mirrored from UserProvider for view
     * resolution (SYD-2080). User-scoped, not form-scoped: UserProvider is its
     * sole writer (and re-pushes `[]` on signout), so `reset()` must NOT clear
     * it — doing so on an in-place `exitPreview` would strand resolution on an
     * empty group set with no re-push until a full reload.
     */
    userGroups: string[];
    selectedSplitOption: string;
    /**
     * The filter the selected split option resolves to. Defined if and only if
     * `selectedSplitOption` names a `show-value` option; `undefined` for
     * `no-split`. `formActions.setSelectedSplitOption` and this store's
     * load/reset paths are its only writers — the two properties are always
     * written together, so every `selectedSplitOption = 'no-split'` clears this.
     *
     * Stored, not derived: if the selected option later vanishes from the live
     * options (its rows were deleted) the filter PERSISTS and the view shows
     * fewer or zero records, rather than silently self-clearing back to all
     * records. That matches the Select, which already shows the stale value.
     * Do not "repair" it with an effect watching the options.
     */
    splitQuery: SplitQuery | undefined;
    previewMode: boolean;

    isArchivingForm: boolean;
    isUnarchivingForm: boolean;
    isArchiving: boolean;
    isUnarchiving: boolean;

    /**
     * A workflow run is building its per-recipient documents and dispatching
     * them. Every control that changes a table's row scope — the selection
     * checkboxes, the selection-scope bar, the column filter inputs and
     * pagination — is disabled while this is set, because the PDF and XLSX
     * builders read the live selection and filters once per document, tens of
     * seconds after the Run gate counted each recipient's rows. A tick or a
     * filter typed inside that window empties the documents built after it
     * (SYD-2410).
     *
     * `handleRunClick` in `WorkflowParameterContent` is its only writer, set
     * synchronously with `dispatchInFlightRef` and cleared in the same
     * `finally`, so an escaped throw can never leave the grid locked. `reset()`
     * clears it too, for the signout that outlives no run.
     */
    isDispatchingWorkflowRun: boolean;

    /**
     * Required elements that blocked the last gated action. Written by
     * `checkRequiredFields` at the call site; `FormRequiredFieldsModal` is the
     * sole reader and empties it on close, so a non-empty array *is* the open
     * state — there is no separate flag.
     */
    missingRequiredFields: MissingRequiredField[];

    tables: Map<string, FormTableSlot>;
};

type Unsubscribe = () => void;

const createDocumentSlot = <T>(): DocumentState<T> => ({
    loading: true,
    data: null,
    error: null,
});

const createCollectionSlot = <T>(): CollectionSlot<T> => ({
    loading: true,
    docs: proxyMap<string, DocEntry<T>>(),
    docIds: [],
    error: null,
});

const state = proxy<FormStoreState>({
    formId: undefined,
    versionId: undefined,
    uiLayoutId: undefined,

    form: createDocumentSlot<Form>(),
    formVersion: createDocumentSlot<FormVersion>(),
    formType: createDocumentSlot<FormType>(),
    uiLayout: createDocumentSlot<UILayout>(),

    formVersions: createCollectionSlot<FormVersion>(),
    formTypes: createCollectionSlot<FormType>(),
    uiLayouts: createCollectionSlot<UILayout>(),

    visibilityMode: SYSTEM_DEFAULT_VISIBILITY_VIEW,
    sessionViewByFormType: {},
    userGroups: [],
    selectedSplitOption: 'no-split',
    splitQuery: undefined,
    previewMode: false,

    isArchivingForm: false,
    isUnarchivingForm: false,
    isArchiving: false,
    isUnarchiving: false,
    isDispatchingWorkflowRun: false,

    missingRequiredFields: [],

    tables: proxyMap<string, FormTableSlot>(),
});

let formPersist: DocumentPersist<Form> | null = null;
let formUnsub: Unsubscribe | null = null;

let formVersionPersist: DocumentPersist<FormVersion> | null = null;
let formVersionUnsub: Unsubscribe | null = null;

let formTypePersist: DocumentPersist<FormType> | null = null;
let formTypeUnsub: Unsubscribe | null = null;

let uiLayoutPersist: DocumentPersist<UILayout> | null = null;
let uiLayoutUnsub: Unsubscribe | null = null;

let formVersionsPersist: CollectionPersist<FormVersion> | null = null;
let formVersionsUnsub: Unsubscribe | null = null;

let uiLayoutsPersist: CollectionPersist<UILayout> | null = null;
let uiLayoutsUnsub: Unsubscribe | null = null;

let formTypesPersist: CollectionPersist<FormType> | null = null;
let formTypesUnsub: Unsubscribe | null = null;

const tableKey = (parentPath: string, tableId: string): string => `${parentPath}#${tableId}`;

const resetDocument = <T>(slot: DocumentState<T>, loading: boolean): void => {
    slot.data = null;
    slot.loading = loading;
    slot.error = null;
};

const resetCollection = <T>(slot: CollectionSlot<T>, loading: boolean): void => {
    slot.docs.clear();
    slot.docIds.length = 0;
    slot.loading = loading;
    slot.error = null;
};

/**
 * Drops the split selection and the filter it resolved to. The only way this
 * store clears them — keeping the `splitQuery`-iff-`show-value` invariant intact
 * across every navigation, reset, and preview load.
 */
const clearSplitSelection = (): void => {
    state.selectedSplitOption = 'no-split';
    state.splitQuery = undefined;
};

const makeErrorHandler = <T>(slot: DocumentState<T> | CollectionSlot<T>) =>
    (error: Error): void => {
        slot.error = error;
    };

const connectForm = (formId: string | undefined): void => {
    formUnsub?.();
    formUnsub = null;
    formPersist = null;
    resetDocument(state.form, !!formId);

    if (!formId) return;

    const result = persistDocument<Form>(`forms/${formId}`, state.form, makeErrorHandler(state.form));
    formPersist = result.persist;
    formUnsub = result.unsubscribe;
};

const connectFormVersion = (formId: string | undefined, versionId: string | undefined): void => {
    formVersionUnsub?.();
    formVersionUnsub = null;
    formVersionPersist = null;
    resetDocument(state.formVersion, !!(formId && versionId));

    if (!formId || !versionId) return;

    const result = persistDocument<FormVersion>(
        `forms/${formId}/formVersions/${versionId}`,
        state.formVersion,
        makeErrorHandler(state.formVersion),
    );
    formVersionPersist = result.persist;
    formVersionUnsub = result.unsubscribe;
};

const connectFormType = (formTypeId: string | undefined): void => {
    formTypeUnsub?.();
    formTypeUnsub = null;
    formTypePersist = null;
    resetDocument(state.formType, !!formTypeId);

    if (!formTypeId) return;

    const result = persistDocument<FormType>(
        `formTypes/${formTypeId}`,
        state.formType,
        makeErrorHandler(state.formType),
    );
    formTypePersist = result.persist;
    formTypeUnsub = result.unsubscribe;
};

const connectUILayout = (formTypeId: string | undefined, uiLayoutId: string | undefined): void => {
    uiLayoutUnsub?.();
    uiLayoutUnsub = null;
    uiLayoutPersist = null;
    resetDocument(state.uiLayout, !!(formTypeId && uiLayoutId));

    if (!formTypeId || !uiLayoutId) return;

    const result = persistDocument<UILayout & Record<string, unknown>>(
        `formTypes/${formTypeId}/uiLayouts/${uiLayoutId}`,
        state.uiLayout as DocumentState<UILayout & Record<string, unknown>>,
        makeErrorHandler(state.uiLayout),
    );
    uiLayoutPersist = result.persist as DocumentPersist<UILayout>;
    uiLayoutUnsub = result.unsubscribe;
};

const connectFormVersions = (formId: string | undefined): void => {
    formVersionsUnsub?.();
    formVersionsUnsub = null;
    formVersionsPersist = null;
    resetCollection(state.formVersions, !!formId);

    if (!formId) return;

    const result = persistCollection<FormVersion>(
        `forms/${formId}/formVersions`,
        state.formVersions,
        makeErrorHandler(state.formVersions),
    );
    formVersionsPersist = result.persist;
    formVersionsUnsub = result.unsubscribe;
};

const connectUILayouts = (formTypeId: string | undefined): void => {
    uiLayoutsUnsub?.();
    uiLayoutsUnsub = null;
    uiLayoutsPersist = null;
    resetCollection(state.uiLayouts, !!formTypeId);

    if (!formTypeId) return;

    const result = persistCollection<UILayout & Record<string, unknown>>(
        `formTypes/${formTypeId}/uiLayouts`,
        state.uiLayouts as CollectionSlot<UILayout & Record<string, unknown>>,
        makeErrorHandler(state.uiLayouts),
    );
    uiLayoutsPersist = result.persist as CollectionPersist<UILayout>;
    uiLayoutsUnsub = result.unsubscribe;
};

const registerTable = (parentPath: string, tableId: string): FormTableSlot => {
    const key = tableKey(parentPath, tableId);
    const existing = state.tables.get(key);
    if (existing) return existing;
    const slot: FormTableSlot = {
        ui: createArTableState(),
    };
    state.tables.set(key, slot);
    return slot;
};

const clearAllTables = (): void => {
    state.tables.clear();
};

const connectFormTypes = (enabled: boolean): void => {
    if (enabled && formTypesPersist) return;
    formTypesUnsub?.();
    formTypesUnsub = null;
    formTypesPersist = null;
    resetCollection(state.formTypes, enabled);

    if (!enabled) return;

    const result = persistCollection<FormType>(
        'formTypes',
        state.formTypes,
        makeErrorHandler(state.formTypes),
    );
    formTypesPersist = result.persist;
    formTypesUnsub = result.unsubscribe;
};

const availableViewIdsForFormType = (
    orgParams: OrganizationParameters,
    formTypeId: string,
): Set<VisibilityTypeId> => {
    const ids = new Set<VisibilityTypeId>();
    for (const d of DEFAULT_VISIBILITY_TYPES) ids.add(d.id);
    for (const v of orgParams.visibilityTypes ?? []) if (v?.id) ids.add(v.id);
    for (const v of orgParams.visibilityTypesByFormType?.[formTypeId] ?? []) if (v?.id) ids.add(v.id);
    return ids;
};

/**
 * Points `visibilityMode` at the right view for the current form type
 * (SYD-2080). A form type the user has explicitly chosen a view for restores
 * that remembered view; otherwise the view is resolved from role/form-type
 * defaults. Auto-resolution never records into `sessionViewByFormType`, so it
 * re-runs as inputs (org params, user groups) settle — e.g. on a cold deep
 * link — while a recorded choice is left untouched. Writes are guarded so a
 * no-op change never re-triggers the subscribe.
 */
const applyVisibilityForFormType = (formTypeId: string): void => {
    const remembered = state.sessionViewByFormType[formTypeId];
    if (remembered !== undefined) {
        if (state.visibilityMode !== remembered) state.visibilityMode = remembered;
        return;
    }

    const orgParams = orgParametersStore.state.data;
    if (!orgParams) return;

    const resolved = resolveVisibilityView({
        userGroups: state.userGroups,
        defaults: orgParams.visibilityDefaultsByFormType?.[formTypeId],
        availableViewIds: availableViewIdsForFormType(orgParams, formTypeId),
        systemDefault: SYSTEM_DEFAULT_VISIBILITY_VIEW,
    });
    if (state.visibilityMode !== resolved) state.visibilityMode = resolved;
};

let lastFormId: string | undefined = state.formId;
let lastVersionId: string | undefined = state.versionId;
let lastUILayoutId: string | undefined = state.uiLayoutId;
let lastFormTypeId: string | undefined = state.form.data?.formType;

subscribe(state, () => {
    if (state.previewMode) return;
    const formId = state.formId;
    const versionId = state.versionId;
    const uiLayoutId = state.uiLayoutId;
    const formTypeId = state.form.data?.formType;

    if (formId !== lastFormId) {
        connectForm(formId);
        connectFormVersions(formId);
        connectFormVersion(formId, versionId);
        clearAllTables();
        formUndoStack.clear();
        formRecoveryStore.clear();
        clearSplitSelection();
        lastFormId = formId;
        lastVersionId = versionId;
    } else if (versionId !== lastVersionId) {
        connectFormVersion(formId, versionId);
        clearAllTables();
        formUndoStack.clear();
        formRecoveryStore.clear();
        clearSplitSelection();
        lastVersionId = versionId;
    }

    if (formTypeId !== lastFormTypeId) {
        connectFormType(formTypeId);
        connectUILayouts(formTypeId);
        connectUILayout(formTypeId, uiLayoutId);
        clearSplitSelection();
        lastFormTypeId = formTypeId;
        lastUILayoutId = uiLayoutId;
    } else if (uiLayoutId !== lastUILayoutId) {
        connectUILayout(formTypeId, uiLayoutId);
        lastUILayoutId = uiLayoutId;
    }

    if (formTypeId) applyVisibilityForFormType(formTypeId);
});

/**
 * The main subscribe only fires on `formStore.state` mutations, so it re-runs
 * resolution as `userGroups` (mirrored into this store) settle — but never when
 * the separate `orgParametersStore` doc finishes loading. Subscribe to that doc
 * too so a cold deep link whose org params arrive last still lands on the
 * role/form-type default instead of being stranded on the system default
 * (SYD-2080). `applyVisibilityForFormType` is write-guarded, so this is a no-op
 * once the resolved view is already in place.
 */
subscribe(orgParametersStore.state, () => {
    if (state.previewMode) return;
    const formTypeId = state.form.data?.formType;
    if (formTypeId) applyVisibilityForFormType(formTypeId);
});

const activate = (): (() => void) => {
    connectFormTypes(true);
    return () => {
        connectFormTypes(false);
    };
};

const detachAll = (): void => {
    connectForm(undefined);
    connectFormVersion(undefined, undefined);
    connectFormType(undefined);
    connectUILayout(undefined, undefined);
    connectFormVersions(undefined);
    connectUILayouts(undefined);
    connectFormTypes(false);

    lastFormId = undefined;
    lastVersionId = undefined;
    lastUILayoutId = undefined;
    lastFormTypeId = undefined;
};

/**
 * Tears down every Firestore listener and resets the form store to its
 * initial shape. Call on user signout to prevent prior-user data from
 * leaking into the next session. Also the teardown for `loadPreview`.
 */
const reset = (): void => {
    detachAll();

    state.formId = undefined;
    state.versionId = undefined;
    state.uiLayoutId = undefined;
    state.visibilityMode = SYSTEM_DEFAULT_VISIBILITY_VIEW;
    state.sessionViewByFormType = {};
    clearSplitSelection();
    state.previewMode = false;
    state.isArchivingForm = false;
    state.isUnarchivingForm = false;
    state.isArchiving = false;
    state.isUnarchiving = false;
    state.isDispatchingWorkflowRun = false;
    state.missingRequiredFields = [];
    state.tables.clear();
    formUndoStack.clear();
    formRecoveryStore.clear();
};

/**
 * Loads a read-only mock form into the singleton for the layout-editor
 * preview. Detaches any live listeners, then sets `previewMode` — which
 * gates the subscribe callback so these sentinel ids never spawn a
 * `forms/preview` listener that would clobber the mock — and fills the
 * document slots. Teardown is `reset()`.
 */
const loadPreview = (input: {
    form: Form;
    formType: FormType;
    formVersion: FormVersion;
    visibilityMode: VisibilityTypeId;
}): void => {
    detachAll();

    state.previewMode = true;
    state.formId = 'preview';
    state.versionId = 'preview';
    state.uiLayoutId = undefined;

    state.form.data = input.form;
    state.form.loading = false;
    state.form.error = null;

    state.formType.data = input.formType;
    state.formType.loading = false;
    state.formType.error = null;

    state.formVersion.data = input.formVersion;
    state.formVersion.loading = false;
    state.formVersion.error = null;

    state.visibilityMode = input.visibilityMode;
    clearSplitSelection();
};

/**
 * Teardown for `loadPreview`. If the URL listener has already navigated the
 * singleton onto a real form by the time the preview unmounts (`formId` is a
 * real id, not the `preview` sentinel), a blind `reset()` would clobber that
 * `formId` and strand the destination form with no listeners. In that case we
 * only drop `previewMode` — which lets the gated subscribe attach live
 * listeners that overwrite the mock slots. Otherwise (preview closed in place,
 * or navigation to a non-form route) fully `reset()`.
 *
 * That branch clears the preview's `splitQuery` only indirectly, and depends on
 * it: writing `previewMode` is itself the mutation that fires the subscribe,
 * which then sees `formId !== lastFormId` (`detachAll` zeroed the trackers) and
 * clears through `clearSplitSelection`.
 */
const exitPreview = (): void => {
    if (state.formId && state.formId !== 'preview') {
        state.previewMode = false;
        return;
    }
    reset();
};

export const formStore = {
    state,
    activate,
    reset,
    loadPreview,
    exitPreview,
    getFormPersist: (): DocumentPersist<Form> | null => formPersist,
    getFormVersionPersist: (): DocumentPersist<FormVersion> | null => formVersionPersist,
    getFormTypePersist: (): DocumentPersist<FormType> | null => formTypePersist,
    getUILayoutPersist: (): DocumentPersist<UILayout> | null => uiLayoutPersist,
    getFormVersionsPersist: (): CollectionPersist<FormVersion> | null => formVersionsPersist,
    getUILayoutsPersist: (): CollectionPersist<UILayout> | null => uiLayoutsPersist,
    getFormTypesPersist: (): CollectionPersist<FormType> | null => formTypesPersist,
    registerTable,
    getTableSlot: (parentPath: string, tableId: string): FormTableSlot | null =>
        state.tables.get(tableKey(parentPath, tableId)) ?? null,
    tableKey,
    navigate: null as NavigateFunction | null,
};
